import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router';
import { Link } from 'react-router-dom';
import * as Yup from 'yup';

import {
  Container,
  FormikWrapper,
  FormEmail as EmailField,
} from '@uncinc/uncinc-react-kitchen-sink';

import {
  forgotPassword,
  UI_OPERATION_STATUS_REQUEST,
  UI_OPERATION_STATUS_ERROR,
  UI_OPERATION_STATUS_SUCCESS,
  UI_OPERATION_STATUS_REASON_UNKNOWN,
  setOperationStatus,
} from '@uncinc/react-drupal-core';

import { Button, Main } from '../../../components';
import RequestPasswordSuccess from './Success';
import useClasses from '../../../hooks/useClasses';
import useOperationStatus from '../../../hooks/useOperationStatus';
import Page from '../../../containers/Page';
import useRoutes from '../../../hooks/useRoutes';
import useToast from '../../../hooks/useToast';

import './style.scss';

const RequestPassword = () => {
  const { login } = useRoutes();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const history = useHistory();
  const { buildClass } = useClasses('RequestPassword');
  const forgotPasswordOperationStatus = useOperationStatus('user', 'forgot_password');
  const toast = useToast('error');

  const [submittedEmail, setSubmittedEmail] = useState();
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);

  useEffect(() => {
    if (forgotPasswordOperationStatus === UI_OPERATION_STATUS_ERROR) {
      toast.show(t('pages.forgot_password.error'));
    }

    if (forgotPasswordOperationStatus === UI_OPERATION_STATUS_SUCCESS) {
      setShowSuccessMessage(true);
    }

    if (forgotPasswordOperationStatus !== UI_OPERATION_STATUS_REQUEST) {
      // Consume the operation status so the toast won't show again.
      dispatch(setOperationStatus('user', 'forgot_password', null, UI_OPERATION_STATUS_REASON_UNKNOWN));
    }
  }, [forgotPasswordOperationStatus, toast, t, dispatch]);

  const validationSchema = Yup.object().shape({
    email: Yup.string()
      .email(t('pages.forgot_password.fields.email.validation.email'))
      .required(t('pages.forgot_password.fields.email.validation.required')),
  });

  const onSubmit = ({ email }) => {
    dispatch(forgotPassword(email));
    setSubmittedEmail(email);
  };

  return (
    <Page className={buildClass()} hideMenuMain title={t('pages.forgot_password.title')}>
      <Container>
        <div className="row">
          <div className="col-md-8 col-lg-6 offset-md-2 offset-md-3 py-4 py-md-8 py-lg-12">
            <header className={buildClass('header')}>
              <Link
                className="icon-before icon-chevron icon-orientation-left"
                onClick={() => {
                  history.push(login);
                }}
              >
                {t('buttons.back')}
              </Link>
            </header>

            <Main className="content">
              <h1 className={buildClass('title')}>
                {t('pages.forgot_password.title')}
              </h1>

              {showSuccessMessage ? (
                <RequestPasswordSuccess email={submittedEmail} />
              ) : (
                <FormikWrapper
                  initialValues={{ email: '' }}
                  validationSchema={validationSchema}
                  onSubmit={(values, { setSubmitting }) => {
                    onSubmit(values);
                    setSubmitting(false);
                  }}
                  validateOnBlur={false}
                  validateOnChange={false}
                >
                  <EmailField
                    fieldName="email"
                    title={t('pages.forgot_password.fields.email.label')}
                    placeholder={t('pages.forgot_password.fields.email.placeholder')}
                    required
                  />
                  <Button
                    htmlType="submit"
                    className={buildClass('submit')}
                    buttonState={forgotPasswordOperationStatus === UI_OPERATION_STATUS_REQUEST && 'disabled'}
                  >
                    {t('buttons.submit')}
                  </Button>
                </FormikWrapper>
              )}
            </Main>
          </div>
        </div>
      </Container>
    </Page>
  );
};

export default RequestPassword;
